import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, message, Modal, Form, DatePicker, Select, Input, Spin, Button } from 'antd'
import { VideoCameraOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  Calendar,
  type CalendarEvent,
  type CalendarDoctor,
  STATUS_CONFIG,
  TYPE_LABELS,
} from '@perfectamed/calendar'
import '@perfectamed/calendar/styles.css'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

const getUserRole = (): string | null => {
  return localStorage.getItem('admin_user_role')
}

interface ApiDoctor {
  id: string
  firstName: string | null
  lastName: string | null
  specialty: string | null
  image: string | null
}

// Response format from /admin/consultations/scheduled (nurse/admin)
interface ApiScheduledConsultation {
  id: string
  patient: {
    id: string
    name: string
    email: string
    phone: string | null
    image: string | null
  }
  doctor: {
    id: string
    name: string
    image: string | null
  }
  scheduledAt: string
  duration: number
  status: string
  statusLabel: string
  roomUrl: string | null
  type: string
  typeLabel: string
}

// Response format from /admin/consultations/my (doctor)
interface ApiDoctorAppointment {
  id: string
  patientId: string
  patientName: string
  patientEmail: string
  patientPhone: string | null
  patientImage: string | null
  type: string
  typeLabel: string
  scheduledAt: string
  duration: number
  status: string
  statusLabel: string
  roomUrl: string | null
  patientNotes: string | null
  doctorNotes: string | null
}

export function CalendarPage() {
  const navigate = useNavigate()
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [doctors, setDoctors] = useState<CalendarDoctor[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [form] = Form.useForm()

  const userRole = useMemo(() => getUserRole(), [])
  const isDoctor = userRole === 'DOCTOR'
  const isNurseOrAdmin = userRole === 'ADMIN' || userRole === 'NURSE'

  // Fetch doctors for filtering (only for nurse/admin)
  const fetchDoctors = useCallback(async () => {
    if (isDoctor) return // Doctors don't need doctor filter

    try {
      const res = await fetch(`${API_URL}/admin/doctors?pageSize=100`, {
        headers: getAuthHeaders(),
      })
      if (!res.ok) throw new Error('Failed to fetch doctors')
      const json = await res.json()
      const doctorsList: CalendarDoctor[] = (json.data || []).map((doc: ApiDoctor) => ({
        id: doc.id,
        name: doc.lastName || doc.firstName || 'N/A',
        title: 'Dr.',
        specialty: doc.specialty || undefined,
      }))
      setDoctors(doctorsList)
    } catch (error) {
      console.error('Error fetching doctors:', error)
    }
  }, [isDoctor])

  // Fetch consultations for calendar - different endpoints for different roles
  const fetchConsultations = useCallback(async () => {
    setLoading(true)
    try {
      if (isDoctor) {
        // Doctor sees their own appointments
        const res = await fetch(`${API_URL}/admin/consultations/my`, {
          headers: getAuthHeaders(),
        })
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to fetch consultations')
        }
        const appointments: ApiDoctorAppointment[] = await res.json()

        const calendarEvents: CalendarEvent[] = appointments.map((apt) => ({
          id: apt.id,
          title: apt.typeLabel || TYPE_LABELS[apt.type as keyof typeof TYPE_LABELS]?.labelSr || apt.type,
          start: new Date(apt.scheduledAt),
          end: new Date(new Date(apt.scheduledAt).getTime() + apt.duration * 60000),
          duration: apt.duration,
          status: apt.status as keyof typeof STATUS_CONFIG,
          type: apt.type as keyof typeof TYPE_LABELS,
          doctor: {
            id: 'self',
            name: 'Ja',
            title: 'Dr.',
          },
          patient: {
            id: apt.patientId,
            name: apt.patientName,
          },
          roomUrl: apt.roomUrl || undefined,
        }))

        setEvents(calendarEvents)
      } else {
        // Nurse/Admin sees all scheduled consultations
        const res = await fetch(`${API_URL}/admin/consultations/scheduled?pageSize=200`, {
          headers: getAuthHeaders(),
        })
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to fetch consultations')
        }
        const json = await res.json()

        const calendarEvents: CalendarEvent[] = (json.data || []).map((c: ApiScheduledConsultation) => ({
          id: c.id,
          title: c.typeLabel || TYPE_LABELS[c.type as keyof typeof TYPE_LABELS]?.labelSr || c.type,
          start: new Date(c.scheduledAt),
          end: new Date(new Date(c.scheduledAt).getTime() + c.duration * 60000),
          duration: c.duration,
          status: c.status as keyof typeof STATUS_CONFIG,
          type: c.type as keyof typeof TYPE_LABELS,
          doctor: {
            id: c.doctor.id,
            name: c.doctor.name,
            title: 'Dr.',
          },
          patient: {
            id: c.patient.id,
            name: c.patient.name,
          },
          roomUrl: c.roomUrl || undefined,
        }))

        setEvents(calendarEvents)
      }
    } catch (error) {
      console.error('Error fetching consultations:', error)
      message.error('Greska pri ucitavanju konsultacija')
    } finally {
      setLoading(false)
    }
  }, [isDoctor])

  useEffect(() => {
    fetchDoctors()
    fetchConsultations()
  }, [fetchDoctors, fetchConsultations])

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event)
    setIsDetailModalOpen(true)
  }

  const handleSlotClick = (date: Date, hour: number, minute: number) => {
    // Only nurse/admin can create appointments
    if (!isNurseOrAdmin) return

    form.resetFields()
    form.setFieldsValue({
      scheduledAt: dayjs(date).hour(hour).minute(minute),
      duration: 30,
      doctorId: doctors[0]?.id,
      type: 'INITIAL_CONSULTATION',
    })
    setIsCreateModalOpen(true)
  }

  const handleEventDrop = async (event: CalendarEvent, newStart: Date) => {
    // Only nurse/admin can reschedule
    if (!isNurseOrAdmin) {
      message.warning('Lekari ne mogu pomerati termine')
      return
    }

    try {
      const res = await fetch(`${API_URL}/admin/consultations/${event.id}/reschedule`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          scheduledAt: newStart.toISOString(),
        }),
      })

      if (!res.ok) {
        const error = await res.json().catch(() => ({}))
        throw new Error(error.message || 'Failed to reschedule')
      }

      // Update local state optimistically
      setEvents((prev) =>
        prev.map((e) =>
          e.id === event.id
            ? { ...e, start: newStart, end: new Date(newStart.getTime() + e.duration * 60000) }
            : e
        )
      )
      message.success(`Termin premesten: ${event.patient.name}`)
    } catch (error) {
      message.error('Greska pri pomeranju termina')
      // Refetch to revert
      fetchConsultations()
    }
  }

  const handleCreateEvent = async () => {
    try {
      const values = await form.validateFields()
      const startDate = values.scheduledAt.toDate()

      const res = await fetch(`${API_URL}/admin/consultations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          patientName: values.patientName,
          patientEmail: values.patientEmail,
          doctorId: values.doctorId,
          scheduledAt: startDate.toISOString(),
          duration: values.duration,
          type: values.type,
          notes: values.notes,
        }),
      })

      if (!res.ok) {
        const error = await res.json().catch(() => ({}))
        throw new Error(error.message || 'Failed to create consultation')
      }

      setIsCreateModalOpen(false)
      message.success('Termin kreiran!')
      fetchConsultations()
    } catch (error) {
      if (error instanceof Error && error.message) {
        message.error(error.message)
      }
    }
  }

  const handleViewPatient = () => {
    if (selectedEvent) {
      navigate(`/patients/${selectedEvent.patient.id}`)
    }
  }

  const handleJoinCall = () => {
    if (selectedEvent?.roomUrl) {
      window.open(selectedEvent.roomUrl, '_blank')
    }
  }

  const handleViewMyConsultation = () => {
    if (selectedEvent) {
      navigate(`/my-consultations/${selectedEvent.id}`)
    }
  }

  if (loading && events.length === 0) {
    return (
      <Card title={isDoctor ? 'Moj kalendar' : 'Kalendar konsultacija'}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Card>
    )
  }

  return (
    <Card
      title={isDoctor ? 'Moj kalendar' : 'Kalendar konsultacija'}
      extra={
        <span style={{ fontSize: 12, color: '#666' }}>
          {events.length} {isDoctor ? 'mojih termina' : 'zakazanih termina'}
        </span>
      }
      styles={{ body: { padding: 0 } }}
    >
      <Calendar
        events={events}
        doctors={isDoctor ? undefined : doctors}
        defaultView="week"
        locale="sr"
        editable={isNurseOrAdmin}
        startHour={0}
        endHour={24}
        slotDuration={30}
        onEventClick={handleEventClick}
        onSlotClick={isNurseOrAdmin ? handleSlotClick : undefined}
        onEventDrop={isNurseOrAdmin ? handleEventDrop : undefined}
      />

      {/* Event Detail Modal */}
      <Modal
        title="Detalji termina"
        open={isDetailModalOpen}
        onCancel={() => setIsDetailModalOpen(false)}
        footer={
          isDoctor ? (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsDetailModalOpen(false)}>Zatvori</Button>
              {selectedEvent?.roomUrl && (
                <Button type="primary" icon={<VideoCameraOutlined />} onClick={handleJoinCall}>
                  Pristupi pozivu
                </Button>
              )}
              <Button type="default" onClick={handleViewMyConsultation}>
                Detalji konsultacije
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsDetailModalOpen(false)}>Zatvori</Button>
              <Button type="primary" onClick={handleViewPatient}>
                Pogledaj pacijenta
              </Button>
            </div>
          )
        }
      >
        {selectedEvent && (
          <div style={{ lineHeight: 2 }}>
            <p>
              <strong>Pacijent:</strong> {selectedEvent.patient.name}
            </p>
            {!isDoctor && (
              <p>
                <strong>Lekar:</strong> {selectedEvent.doctor.title} {selectedEvent.doctor.name}
              </p>
            )}
            <p>
              <strong>Tip:</strong> {TYPE_LABELS[selectedEvent.type]?.labelSr || selectedEvent.type}
            </p>
            <p>
              <strong>Status:</strong> {STATUS_CONFIG[selectedEvent.status]?.labelSr || selectedEvent.status}
            </p>
            <p>
              <strong>Vreme:</strong>{' '}
              {selectedEvent.start.toLocaleString('sr-Latn-RS', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
            <p>
              <strong>Trajanje:</strong> {selectedEvent.duration} min
            </p>
            {selectedEvent.roomUrl && (
              <p>
                <strong>Video link:</strong>{' '}
                <a href={selectedEvent.roomUrl} target="_blank" rel="noopener noreferrer">
                  Pristupi pozivu
                </a>
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* Create Event Modal - only for nurse/admin */}
      {isNurseOrAdmin && (
        <Modal
          title="Zakazi novi termin"
          open={isCreateModalOpen}
          onCancel={() => setIsCreateModalOpen(false)}
          onOk={handleCreateEvent}
          okText="Zakazi"
          cancelText="Otkazi"
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              name="patientName"
              label="Ime pacijenta"
              rules={[{ required: true, message: 'Unesite ime pacijenta' }]}
            >
              <Input placeholder="npr. Marko Petrovic" />
            </Form.Item>

            <Form.Item
              name="patientEmail"
              label="Email pacijenta"
              rules={[
                { required: true, message: 'Unesite email pacijenta' },
                { type: 'email', message: 'Unesite validan email' },
              ]}
            >
              <Input placeholder="npr. marko@example.com" />
            </Form.Item>

            <Form.Item name="doctorId" label="Lekar" rules={[{ required: true }]}>
              <Select placeholder="Izaberite lekara">
                {doctors.map((doc) => (
                  <Select.Option key={doc.id} value={doc.id}>
                    {doc.title} {doc.name} {doc.specialty && `- ${doc.specialty}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="type" label="Tip konsultacije" rules={[{ required: true }]}>
              <Select placeholder="Izaberite tip">
                {Object.entries(TYPE_LABELS).map(([key, value]) => (
                  <Select.Option key={key} value={key}>
                    {value.labelSr}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="scheduledAt" label="Datum i vreme" rules={[{ required: true }]}>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD.MM.YYYY HH:mm"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name="duration" label="Trajanje (minuta)" rules={[{ required: true }]}>
              <Select>
                <Select.Option value={15}>15 min</Select.Option>
                <Select.Option value={30}>30 min</Select.Option>
                <Select.Option value={45}>45 min</Select.Option>
                <Select.Option value={60}>60 min</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item name="notes" label="Napomena">
              <Input.TextArea rows={2} placeholder="Opciona napomena..." />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </Card>
  )
}

export default CalendarPage
